/**
 * MailTemplateModal
 *
 * Fullscreen modal for viewing mail templates with front/back flip and
 * optional Canva link. Locks body scroll while open.
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink } from 'lucide-react';

export default function MailTemplateModal({
  template,
  isOpen,
  onClose
}) {
  const [showFront, setShowFront] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);

  // Reset to front image when template changes
  useEffect(() => {
    if (template) {
      setShowFront(true);
    }
  }, [template]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !template) return null;

  const handleFlip = () => {
    if (!template.backImageUrl) return;
    setImageLoading(true);
    setShowFront(!showFront);
    // Reset loading after transition
    setTimeout(() => setImageLoading(false), 300);
  };

  const handleCanvaClick = () => {
    if (template.canvaLink) {
      window.open(template.canvaLink, '_blank', 'noopener,noreferrer');
    }
  };

  const currentImage = showFront ? 
    (template.frontImageUrl || template.previewImageURL || template.imageUrl) : 
    template.backImageUrl;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-6xl mx-4 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Image Display Area - Moderate Size */}
        <div className="relative bg-gray-50 dark:bg-gray-800">
          <div 
            className="flex items-center justify-center min-h-[60vh] max-h-[70vh] p-4 cursor-pointer"
            onClick={handleFlip}
            title={template.backImageUrl ? "Click to flip" : ""}
          >
            {currentImage ? (
              <div className="relative max-w-full max-h-full">
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 z-10 rounded-lg">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                  </div>
                )}
                <img
                  src={currentImage}
                  alt={`${template.title || template.company} - ${showFront ? 'Front' : 'Back'}`}
                  className={`max-w-full max-h-full object-contain rounded-lg shadow-2xl transition-all duration-300 ${
                    imageLoading ? 'opacity-50 scale-98' : 'opacity-100 scale-100'
                  } ${template.backImageUrl ? 'hover:shadow-3xl hover:scale-[1.02]' : ''}`}
                  onLoad={() => setImageLoading(false)}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-96 text-gray-400 dark:text-gray-500">
                <div className="text-center">
                  <div className="text-4xl mb-2">📭</div>
                  <p>No image available</p>
                </div>
              </div>
            )}
          </div>


        </div>

        {/* Footer with three sections - Mobile optimized */}
        <div className="flex flex-col sm:flex-row items-center justify-between p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 gap-4 sm:gap-6">
          {/* Mobile: Stack vertically, Desktop: Three columns */}
          
          {/* Left: Click to flip - Hidden on mobile when stacked */}
          <div className="hidden sm:flex sm:flex-shrink-0 sm:w-20">
            {template.backImageUrl ? (
              <button
                onClick={handleFlip}
                className="inline-flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Flip
              </button>
            ) : (
              <div className="w-full"></div>
            )}
          </div>
          
          {/* Center: Edit in Canva - Takes most of the space */}
          <div className="w-full sm:flex-1 sm:flex sm:justify-center sm:max-w-md">
            {template.canvaLink ? (
              <button
                onClick={handleCanvaClick}
                className="canva-button w-full py-2.5 md:py-3 px-8 md:px-12 rounded-md text-sm md:text-base font-medium text-white transition-colors flex items-center justify-center"
                style={{
                  background: "linear-gradient(45deg, #00C4CC, #7D2AE8)",
                  boxShadow: "0 2px 6px rgba(0, 196, 204, 0.3)"
                }}
              >
                Edit in Canva
              </button>
            ) : (
              <div className="text-gray-500 dark:text-gray-400 text-sm text-center">
                No Canva link available
              </div>
            )}
          </div>
          
          {/* Right: Current side indicator - Hidden on mobile when stacked */}
          <div className="hidden sm:flex sm:flex-shrink-0 sm:w-20">
            {template.backImageUrl ? (
              <button
                onClick={handleFlip}
                className="inline-flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200 cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {showFront ? 'Front' : 'Back'}
              </button>
            ) : (
              <div className="w-full"></div>
            )}
          </div>
          
          {/* Mobile: Bottom row with flip and side indicator */}
          <div className="flex sm:hidden w-full justify-between items-center">
            {template.backImageUrl ? (
              <>
                                 <button
                   onClick={handleFlip}
                   className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200"
                 >
                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                   </svg>
                   Flip
                 </button>
                 <button
                   onClick={handleFlip}
                   className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200 cursor-pointer"
                 >
                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                   </svg>
                   {showFront ? 'Front' : 'Back'}
                 </button>
              </>
            ) : (
              <div></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at the end of the body
  return createPortal(modalContent, document.body);
} 